import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { FaUser, FaPhone, FaCheck, FaPlus, FaTrash, FaEdit, FaCalendarAlt, FaMapMarkerAlt } from "react-icons/fa";
import { APP_PATH } from "../../../api/backendPath";
import logo from "../../../assets/images/logo.png";
import axios from "axios";
import { toast } from '../../../components/toast';
import { formatDateNumeric as tzDateNumeric } from "../../../utils/datetime";
import { formatWholeAmount } from "../../../utils/amountFormat";
import PublicPatientName from "../../../components/booking-patient-cells/PublicPatientName";

const PublicPhysioSessions = () => {
  const { id: orderId } = useParams();

  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sessionsSaved, setSessionsSaved] = useState(false);

  const [sessionDate, setSessionDate] = useState("");
  const [sessionTime, setSessionTime] = useState("");

  const [servicesCatalog, setServicesCatalog] = useState([]);

  const [showAddRow, setShowAddRow] = useState(false);
  const [newService, setNewService] = useState({ serviceName: "", numberOfSessions: "1", location: "HOME_VISIT", price: "" });

  const [editingServiceId, setEditingServiceId] = useState(null);
  const [editData, setEditData] = useState({});

  const API_BASE = process.env.REACT_APP_API_URL;

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}${APP_PATH.physioPublicOrder}/${orderId}`);
      if (response?.data?.status && response.data.data) {
        const data = response.data.data;
        setOrderDetails(data);
        if (data.sessions_updated_by_therapist) {
          setSessionsSaved(true);
        }
        if (data.scheduledDate) setSessionDate(data.scheduledDate);
        if (data.scheduledTime) setSessionTime(data.scheduledTime.substring(0, 5));
      } else {
        setError("Order not found");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load order details");
    } finally {
      setLoading(false);
    }
  };

  const fetchServicesCatalog = async () => {
    try {
      const response = await axios.get(`${API_BASE}${APP_PATH.physioPublicOrder}/${orderId}/services-catalog`);
      if (response?.data?.status && response.data.data) {
        const data = response.data.data;
        setServicesCatalog(data.services || (Array.isArray(data) ? data : []));
      }
    } catch (err) {
      console.error("Failed to fetch services catalog:", err);
    }
  };

  useEffect(() => {
    if (orderId) {
      fetchOrderDetails();
      fetchServicesCatalog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const handleAddService = async () => {
    if (!newService.serviceName) { toast.error("Please select a service"); return; }
    if (!newService.numberOfSessions || parseInt(newService.numberOfSessions) <= 0) { toast.error("Please enter number of sessions"); return; }

    try {
      const response = await axios.post(`${API_BASE}${APP_PATH.physioPublicOrder}/${orderId}/add-service`, {
        serviceName: newService.serviceName,
        serviceCode: servicesCatalog.find(s => s.test_name === newService.serviceName)?.service_code || "",
        numberOfSessions: parseInt(newService.numberOfSessions),
        // Location is always Home Visit and cannot be changed by the therapist.
        location: "HOME_VISIT",
        price: parseInt(newService.price),
      });
      if (response?.data?.status) {
        toast.success("Service added");
        setShowAddRow(false);
        setNewService({ serviceName: "", numberOfSessions: "1", location: "HOME_VISIT", price: "" });
        fetchOrderDetails();
      } else {
        toast.error(response?.data?.message || "Failed to add service");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to add service");
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm("Are you sure you want to remove this service?")) return;
    try {
      const response = await axios.delete(`${API_BASE}${APP_PATH.physioPublicOrder}/${orderId}/delete-service/${serviceId}`);
      if (response?.data?.status) {
        toast.success("Service removed");
        fetchOrderDetails();
      } else {
        toast.error(response?.data?.message || "Failed to remove service");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to remove service");
    }
  };

  const handleSaveEdit = async (serviceId) => {
    if (!editData.numberOfSessions || parseInt(editData.numberOfSessions) <= 0) { toast.error("Please enter number of sessions"); return; }

    try {
      const response = await axios.put(`${API_BASE}${APP_PATH.physioPublicOrder}/${orderId}/update-service/${serviceId}`, {
        numberOfSessions: parseInt(editData.numberOfSessions),
        // Location is always Home Visit and cannot be changed by the therapist.
        location: "HOME_VISIT",
        price: parseInt(editData.price),
      });
      if (response?.data?.status) {
        toast.success("Service updated");
        setEditingServiceId(null);
        setEditData({});
        fetchOrderDetails();
      } else {
        toast.error(response?.data?.message || "Failed to update service");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update service");
    }
  };

  const handleSubmitSessions = async () => {
    const services = orderDetails?.services || [];
    if (services.length === 0) { toast.error("Please add at least one service"); return; }

    for (const svc of services) {
      if (!svc.number_of_sessions || svc.number_of_sessions <= 0) {
        toast.error(`Please set number of sessions for "${svc.service_name}"`);
        return;
      }
    }

    setSaving(true);
    try {
      const sessionData = services.map(svc => ({
        serviceId: svc.id,
        serviceName: svc.service_name,
        numberOfSessions: svc.number_of_sessions,
      }));

      const response = await axios.post(`${API_BASE}${APP_PATH.physioPublicOrder}/${orderId}/update-sessions`, {
        sessions: sessionData,
      });
      if (response?.data?.status) {
        toast.success("Assessment completed! The physio center will now send the invoice.");
        setSessionsSaved(true);
        fetchOrderDetails();
      } else {
        toast.error(response?.data?.message || "Failed to save");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const formatPatientName = (patient) => {
    if (!patient) return "Patient";
    return [patient.full_name, patient.surname].filter(Boolean).join(" ") || "Patient";
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', fontFamily: 'Inter, sans-serif' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '40px', height: '40px', border: '4px solid #e5e7eb', borderTop: '4px solid #099494', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
          <p style={{ color: '#6b7280' }}>Loading...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', fontFamily: 'Inter, sans-serif' }}>
        <p style={{ color: '#EF4444', fontSize: '16px' }}>{error}</p>
      </div>
    );
  }

  const services = orderDetails?.services || [];

  // Build a Google Maps link from the patient's address (prefers coordinates,
  // falls back to the joined address string).
  const mapsLink = (addr) => {
    if (!addr) return null;
    if (addr.latitude && addr.longitude) {
      return `https://www.google.com/maps/search/?api=1&query=${addr.latitude},${addr.longitude}`;
    }
    const parts = [addr.houseFlatFloorNo, addr.street, addr.areaDistrict, addr.city, addr.governorate, addr.country]
      .filter(Boolean)
      .join(", ");
    return parts
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(parts)}`
      : null;
  };

  // Show the patient-navigation link under Patient Information only when at
  // least one therapy is a home visit and we have an address to navigate to.
  const patientAddress = orderDetails?.patientAddress || null;
  const hasHomeVisitService = services.some(
    (s) => String(s.location || "").toUpperCase() === "HOME_VISIT",
  );
  const patientNavigationLink = hasHomeVisitService ? mapsLink(patientAddress) : null;

  // Reusable card style
  const cardStyle = {
    background: '#fff',
    borderRadius: '12px',
    padding: '20px',
    border: '1px solid #e5e7eb',
    height: '100%',
    boxSizing: 'border-box'
  };

  const headingStyle = {
    fontSize: '14px',
    fontWeight: 600,
    color: '#099494',
    marginBottom: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    paddingBottom: '10px',
    borderBottom: '1px solid #f3f4f6'
  };

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', background: '#f9fafb', minHeight: '100vh' }}>
      {/* Header with Logo */}
      <div style={{
        background: '#fff',
        borderBottom: '1px solid #e5e7eb',
        padding: '14px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
      }}>
        <img src={logo} alt="Tiryaq" style={{ height: '40px' }} />
      </div>

      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '24px' }}>
        {/* Title */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#1f2937', margin: 0 }}>Assessment - Therapy Details</h1>
          <p style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>Booking: {orderDetails?.bookingNumber || `PT-${orderId}`}</p>
          <p style={{ fontSize: '13px', color: '#099494', marginTop: '10px', background: '#f0fdfa', padding: '8px 14px', borderRadius: '8px', display: 'inline-block' }}>
            Please assess the patient and manage the therapy services
          </p>
        </div>

        {/* Row 1: 3 columns - Patient | Therapist | Schedule */}
        <div data-public-grid-row1 style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '16px',
          marginBottom: '16px'
        }}>
          {/* Patient */}
          <div style={cardStyle}>
            <h3 style={headingStyle}>
              <FaUser /> Patient Information
            </h3>
            {/* Child booking: child + "Child" pill, then the account holder as the
                Guardian. Self booking: the same single name line as before. */}
            <PublicPatientName
              booking={orderDetails}
              guardianName={formatPatientName(orderDetails?.patient)}
              nameStyle={{ fontSize: '16px', fontWeight: 600, color: '#1f2937', margin: '0 0 6px' }}
            />
            {orderDetails?.patient?.mobile_number && (
              <a href={`tel:${orderDetails.patient.mobile_number}`} style={{ fontSize: '14px', color: '#099494', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <FaPhone size={12} /> {orderDetails.patient.mobile_number}
              </a>
            )}
            {/* Home-visit patient navigation: only shown when a therapy is a
                home visit and the patient's address can be mapped. */}
            {patientNavigationLink && (
              <a
                href={patientNavigationLink}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  marginTop: '12px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: '#099494',
                  color: '#fff',
                  textDecoration: 'none',
                  fontSize: '13px',
                  fontWeight: 600,
                  padding: '8px 14px',
                  borderRadius: '8px',
                }}
              >
                <FaMapMarkerAlt size={12} /> Navigate to Patient
              </a>
            )}
          </div>

          {/* Therapist */}
          <div style={cardStyle}>
            <h3 style={headingStyle}>
              <FaUser /> Assigned Therapist
            </h3>
            {(orderDetails?.therapist || orderDetails?.assigned_therapist_name || orderDetails?.assigned_therapist_id) ? (
              <>
                <p style={{ fontSize: '16px', fontWeight: 600, color: '#1f2937', margin: '0 0 6px' }}>
                  {orderDetails.therapist?.name || orderDetails.assigned_therapist_name || `Therapist #${orderDetails.assigned_therapist_id}`}
                </p>
                {orderDetails.therapist?.mobile_number && (
                  <a href={`tel:${orderDetails.therapist.mobile_number}`} style={{ fontSize: '14px', color: '#099494', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FaPhone size={12} /> {orderDetails.therapist.mobile_number}
                  </a>
                )}
                {orderDetails.therapist?.gender && (
                  <p style={{ fontSize: '13px', color: '#6b7280', margin: '6px 0 0' }}>
                    Gender: {orderDetails.therapist.gender.charAt(0).toUpperCase() + orderDetails.therapist.gender.slice(1)}
                  </p>
                )}
              </>
            ) : (
              <p style={{ fontSize: '14px', color: '#9ca3af', margin: 0 }}>Not assigned yet</p>
            )}
          </div>

          {/* Schedule */}
          <div style={cardStyle}>
            <h3 style={headingStyle}>
              <FaCalendarAlt /> Session Schedule
            </h3>
            <div style={{ marginBottom: '10px' }}>
              <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 4px' }}>Session Date</p>
              <p style={{ fontSize: '14px', fontWeight: 500, color: '#1f2937', margin: 0 }}>
                {sessionDate ? tzDateNumeric(new Date(sessionDate)) : 'To be scheduled'}
              </p>
            </div>
            <div>
              <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 4px' }}>Session Time</p>
              <p style={{ fontSize: '14px', fontWeight: 500, color: '#1f2937', margin: 0 }}>
                {sessionTime ? (() => { const [h, m] = sessionTime.split(':'); const hour = parseInt(h); const period = hour >= 12 ? 'PM' : 'AM'; const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour; return `${displayHour}:${m} ${period}`; })() : 'To be scheduled'}
              </p>
            </div>
          </div>
        </div>

        {/* Row 2: Therapy Details Table (full width) */}
        <div style={{ ...cardStyle, marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', paddingBottom: '10px', borderBottom: '1px solid #f3f4f6' }}>
            <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#099494', margin: 0 }}>Therapy Details</h3>
            {!sessionsSaved && (
              <button onClick={() => setShowAddRow(true)} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', color: '#099494', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>
                <FaPlus size={12} /> Add Therapy
              </button>
            )}
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ background: '#099494', color: '#fff' }}>
                  <th style={{ padding: '10px 12px', textAlign: 'left', borderRadius: '6px 0 0 0' }}>Therapy Name</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center' }}>Number of Sessions</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center' }}>Location</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center' }}>Price</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', borderRadius: '0 6px 0 0' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {services.map((svc, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    {editingServiceId === svc.id ? (
                      <>
                        <td style={{ padding: '10px 12px' }}>{svc.service_name}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                          <input type="text" inputMode="numeric" value={editData.numberOfSessions || ""} onChange={(e) => setEditData({ ...editData, numberOfSessions: e.target.value.replace(/[^0-9]/g, "") })} style={{ width: '60px', padding: '6px', borderRadius: '6px', border: '1px solid #d1d5db', textAlign: 'center' }} />
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                          {/* Location is fixed to Home Visit — the therapist cannot change it. */}
                          <span style={{ padding: '3px 10px', borderRadius: '4px', fontSize: '12px', fontWeight: 500, background: '#dbeafe', color: '#1e40af' }}>Home Visit</span>
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                          <span style={{ fontSize: '14px', fontWeight: 500, color: '#1f2937' }}>IQD {formatWholeAmount(editData.price || 0)}</span>
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                            <button onClick={() => handleSaveEdit(svc.id)} style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '12px' }}>Save</button>
                            <button onClick={() => { setEditingServiceId(null); setEditData({}); }} style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '12px' }}>Cancel</button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td style={{ padding: '10px 12px' }}>{svc.service_name}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'center' }}>{svc.number_of_sessions || '—'}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                          <span style={{ padding: '3px 10px', borderRadius: '4px', fontSize: '12px', fontWeight: 500, background: svc.location === 'HOME_VISIT' ? '#dbeafe' : '#f3e8ff', color: svc.location === 'HOME_VISIT' ? '#1e40af' : '#7c3aed' }}>
                            {svc.location === 'HOME_VISIT' ? 'Home Visit' : 'On-Site'}
                          </span>
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'center' }}>IQD {formatWholeAmount(svc.price || 0)}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                          {!sessionsSaved && (
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                              <button onClick={() => { setEditingServiceId(svc.id); setEditData({ numberOfSessions: String(svc.number_of_sessions || ""), location: svc.location || "HOME_VISIT", price: String(Math.round(Number(svc.price || 0))) }); }} style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: '6px', padding: '6px 8px', cursor: 'pointer', color: '#099494' }}><FaEdit size={14} /></button>
                              <button onClick={() => handleDeleteService(svc.id)} style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: '6px', padding: '6px 8px', cursor: 'pointer', color: '#ef4444' }}><FaTrash size={14} /></button>
                            </div>
                          )}
                        </td>
                      </>
                    )}
                  </tr>
                ))}

                {showAddRow && (
                  <tr style={{ background: '#f9fafb' }}>
                    <td style={{ padding: '10px 12px' }}>
                      <select value={newService.serviceName} onChange={(e) => { const selected = servicesCatalog.find(s => s.test_name === e.target.value); setNewService({ ...newService, serviceName: e.target.value, price: selected?.price ? String(Math.round(Number(selected.price))) : "" }); }} style={{ width: '100%', padding: '6px', borderRadius: '6px', border: '1px solid #d1d5db' }}>
                        <option value="">Select Service</option>
                        {servicesCatalog
                          .filter((s) => !services.some((svc) => (svc.service_name || "").trim().toLowerCase() === (s.test_name || "").trim().toLowerCase()))
                          .map((s, i) => (<option key={i} value={s.test_name}>{s.test_name}</option>))}
                      </select>
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                      <input type="text" inputMode="numeric" placeholder="—" value={newService.numberOfSessions} onChange={(e) => setNewService({ ...newService, numberOfSessions: e.target.value.replace(/[^0-9]/g, "") })} style={{ width: '60px', padding: '6px', borderRadius: '6px', border: '1px solid #d1d5db', textAlign: 'center' }} />
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                      {/* Location is fixed to Home Visit — the therapist cannot change it. */}
                      <span style={{ padding: '3px 10px', borderRadius: '4px', fontSize: '12px', fontWeight: 500, background: '#dbeafe', color: '#1e40af' }}>Home Visit</span>
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                      <span style={{ fontSize: '14px', fontWeight: 500, color: '#1f2937' }}>{newService.price ? `IQD ${formatWholeAmount(newService.price)}` : '—'}</span>
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button onClick={handleAddService} style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '12px' }}>Save</button>
                        <button onClick={() => { setShowAddRow(false); setNewService({ serviceName: "", numberOfSessions: "", location: "HOME_VISIT", price: "" }); }} style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '12px' }}>Cancel</button>
                      </div>
                    </td>
                  </tr>
                )}

                {services.length === 0 && !showAddRow && (
                  <tr><td colSpan={5} style={{ padding: '20px', textAlign: 'center', color: '#9ca3af' }}>No services added yet</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {services.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 0', borderTop: '2px solid #099494', marginTop: '12px' }}>
              <span style={{ fontSize: '15px', fontWeight: 700, color: '#1f2937' }}>Total: IQD {formatWholeAmount(services.reduce((sum, s) => sum + Number(s.price || 0), 0))}</span>
            </div>
          )}
        </div>

        {/* Row 3: Submit button */}
        {!sessionsSaved ? (
          <button
            onClick={handleSubmitSessions}
            disabled={saving || services.length === 0}
            style={{
              width: '100%', padding: '16px', borderRadius: '12px', border: 'none',
              fontSize: '16px', fontWeight: 600, cursor: (saving || services.length === 0) ? 'not-allowed' : 'pointer',
              background: '#099494', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              opacity: (saving || services.length === 0) ? 0.6 : 1,
            }}
          >
            <FaCheck /> {saving ? 'Saving...' : 'Submit Assessment'}
          </button>
        ) : (
          <div style={{ textAlign: 'center', padding: '24px', background: '#d1fae5', borderRadius: '12px' }}>
            <FaCheck size={28} color="#065f46" />
            <p style={{ fontSize: '16px', fontWeight: 600, color: '#065f46', margin: '10px 0 4px' }}>Assessment Completed</p>
            <p style={{ fontSize: '13px', color: '#065f46', margin: 0 }}>The physio center will now review and send the invoice to the patient.</p>
          </div>
        )}
      </div>

      {/* Responsive styles */}
      <style>{`
        @media (max-width: 900px) {
          [data-public-grid-row1] { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default PublicPhysioSessions;
